import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { publicGetIntensiveOffer, publicOfferCheckout } from "../lib/api";
import { Button } from "../components/ui/button";
import Seo from "../components/Seo";
import { toast } from "sonner";
import { extractErrorMessage } from "../lib/errors";
import { Phone, Clock, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  } catch {
    return iso;
  }
}

function timeRemaining(expiresAt) {
  if (!expiresAt) return "";
  const diffMs = new Date(expiresAt).getTime() - Date.now();
  if (diffMs <= 0) return "expired";
  const h = Math.floor(diffMs / 3600000);
  const m = Math.floor((diffMs % 3600000) / 60000);
  if (h > 24) return `${Math.floor(h / 24)} day${Math.floor(h / 24) === 1 ? "" : "s"} left`;
  return `${h}h ${m}m left`;
}

export default function IntensiveOfferPage() {
  const { offer_id } = useParams();
  const [search] = useSearchParams();
  const wasCancelled = search.get("cancelled") === "1";
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    publicGetIntensiveOffer(offer_id)
      .then(setOffer)
      .catch((e) => setError(extractErrorMessage(e, "Offer not found")))
      .finally(() => setLoading(false));
  }, [offer_id]);

  const pay = async () => {
    try {
      setPaying(true);
      const res = await publicOfferCheckout(offer_id);
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not start payment"));
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#9CA3AF]">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading your offer…
      </div>
    );
  }

  if (error || !offer) {
    return (
      <>
        <Seo title="Offer not found" robots="noindex,nofollow" />
        <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="offer-not-found">
          <AlertCircle className="h-10 w-10 text-[#DC2626] mx-auto" />
          <h1 className="mt-4 text-2xl font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>We couldn't find that offer</h1>
          <p className="mt-3 text-[#4B5563]">{error || "The link may be incorrect or the offer has been cancelled."}</p>
          <a href="tel:+447472887780" className="mt-6 inline-flex items-center gap-2 text-[#37B160] font-medium">
            <Phone className="h-4 w-4" /> Call Akram on 07472 887780
          </a>
        </div>
      </>
    );
  }

  const isPending = offer.status === "pending";
  const isPaid = offer.status === "paid";
  const isExpired = offer.status === "expired";
  const isCancelled = offer.status === "cancelled";
  const firstName = offer.customer_name?.split(" ")[0] || "there";

  return (
    <>
      <Seo
        title={`Your intensive course offer — Lime Driving School`}
        description={`Payment page for ${offer.customer_name}'s intensive course.`}
        robots="noindex,nofollow"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14" data-testid="intensive-offer-page">
        {wasCancelled && (
          <div className="mb-6 bg-[#FEF3C7] border border-[#F59E0B]/30 text-[#92400E] px-4 py-3 rounded-lg text-sm">
            Payment cancelled — your offer is still available. Tap the pay button whenever you're ready.
          </div>
        )}

        <div className="text-center">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#37B160]">Lime Driving School</span>
          <h1 className="mt-3 text-3xl md:text-4xl font-medium text-[#0A0F00]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Hi {firstName} — your intensive course is ready
          </h1>
          <p className="mt-3 text-[#4B5563]">Akram has put the following together just for you. Review the details below and pay to confirm.</p>
        </div>

        {/* Course details */}
        <div className="mt-10 bg-gradient-to-br from-[#F0FDF4] to-white border border-[#BBF7D0] rounded-2xl p-6 md:p-8">
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#15803D]">Your course</div>
          <h2 className="mt-2 text-2xl md:text-3xl font-medium text-[#0A0F00]" style={{ fontFamily: "Outfit, sans-serif" }}>
            {offer.course_label}
          </h2>
          <div className="mt-4 flex items-center gap-4 flex-wrap text-sm text-[#4B5563]">
            <span>Transmission: <strong className="text-[#0A0F00]">{offer.transmission === "auto" ? "Automatic" : "Manual"}</strong></span>
            <span>·</span>
            <span>{offer.slots.length} × 2-hour sessions</span>
          </div>
          <div className="mt-6 text-5xl font-medium text-[#0A0F00]" style={{ fontFamily: "Outfit, sans-serif" }}>
            £{Number(offer.amount).toFixed(2)}
          </div>
        </div>

        {/* Slots */}
        <div className="mt-8">
          <h3 className="text-lg font-medium text-[#0A0F00] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>Your scheduled slots</h3>
          <div className="bg-white border border-black/5 rounded-2xl divide-y divide-black/5" data-testid="offer-slots-list">
            {offer.slots.map((s, i) => (
              <div key={i} className="p-4 flex items-center justify-between gap-3" data-testid={`offer-slot-${i}`}>
                <div>
                  <div className="font-medium text-[#0A0F00]">{formatDate(s.date)}</div>
                  <div className="text-sm text-[#4B5563] mt-0.5 flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    {s.time} — 2 hours
                  </div>
                </div>
                <CheckCircle2 className="h-5 w-5 text-[#37B160]" />
              </div>
            ))}
          </div>
        </div>

        {offer.notes && (
          <div className="mt-8 bg-[#FEF9F2] border border-[#F59E0B]/20 rounded-xl p-4 text-sm text-[#78350F]">
            <strong>Note from Akram:</strong> {offer.notes}
          </div>
        )}

        {/* Pay action */}
        <div className="mt-10 bg-white border border-black/5 rounded-2xl p-6 md:p-8 text-center">
          {isPending && (
            <>
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#92400E] bg-[#FEF3C7] inline-block px-3 py-1 rounded-full">
                <Clock className="h-3 w-3 inline mr-1" />
                Reserved — {timeRemaining(offer.expires_at)}
              </div>
              <p className="mt-5 text-[#4B5563]">Your slots are held until the countdown ends. Pay now to lock them in permanently.</p>
              <Button
                onClick={pay}
                disabled={paying}
                className="mt-5 bg-[#37B160] hover:bg-[#2D9450] text-white rounded-full px-8 py-6 text-base"
                data-testid="offer-pay-btn"
              >
                {paying ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Redirecting to Stripe…</>
                ) : (
                  <>Pay £{Number(offer.amount).toFixed(2)} with Stripe →</>
                )}
              </Button>
              <p className="mt-4 text-xs text-[#9CA3AF]">Secure payment powered by Stripe. You'll receive a confirmation email with all the details once payment is complete.</p>
            </>
          )}
          {isPaid && (
            <>
              <CheckCircle2 className="h-12 w-12 text-[#37B160] mx-auto" />
              <h3 className="mt-4 text-xl font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>Payment received</h3>
              <p className="mt-2 text-[#4B5563]">Thanks {firstName} — your course is confirmed. Check your email for the full booking details.</p>
            </>
          )}
          {isExpired && (
            <>
              <AlertCircle className="h-10 w-10 text-[#F59E0B] mx-auto" />
              <h3 className="mt-4 text-xl font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>This offer has expired</h3>
              <p className="mt-2 text-[#4B5563]">No worries — just give Akram a call and he'll put a new one together for you.</p>
              <a href="tel:+447472887780" className="mt-5 inline-flex items-center gap-2 text-[#37B160] font-medium">
                <Phone className="h-4 w-4" /> Call Akram on 07472 887780
              </a>
            </>
          )}
          {isCancelled && (
            <>
              <AlertCircle className="h-10 w-10 text-[#DC2626] mx-auto" />
              <h3 className="mt-4 text-xl font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>This offer was cancelled</h3>
              <p className="mt-2 text-[#4B5563]">If this was unexpected, please get in touch and we'll sort it out.</p>
              <a href="tel:+447472887780" className="mt-5 inline-flex items-center gap-2 text-[#37B160] font-medium">
                <Phone className="h-4 w-4" /> Call Akram on 07472 887780
              </a>
            </>
          )}
        </div>

        <div className="mt-10 text-center text-xs text-[#9CA3AF]">
          Questions? Call Akram on <a href="tel:+447472887780" className="underline text-[#37B160]">07472 887780</a> or email <a href="mailto:info@limedrivingschool.com" className="underline text-[#37B160]">info@limedrivingschool.com</a>
        </div>
      </div>
    </>
  );
}
